import { withLineBreaks } from './plainText';

type SpeechKey = 'speechCode' | 'speechTable' | 'speechLink';

/** Blocks the app shows as a card of their own (page, document, picture): nothing to say. */
const ARTIFACT_FENCE = /^(interactive-html|office-artifact|image-artifact)$/i;
const FENCE = /^\s*(`{3,}|~{3,})\s*([\w-]*)/;
const TABLE_DIVIDER = /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/;
const RULE = /^\s*([-*_])(\s*\1){2,}\s*$/;
const LIST_ITEM = /^\s*(?:[-*+]|\d+[.)])\s+/;
const HEADING = /^\s{0,3}#{1,6}\s+/;
const END_PUNCTUATION = /[.!?…:;]$/;

/**
 * An answer in Markdown as plain text to read aloud. Code and tables are not read but pointed
 * at once ("see the code on the screen"), pictures are skipped, web addresses become "link",
 * list items and headings become sentences of their own.
 */
export function speechText(markdown: string, t: (key: SpeechKey) => string): string {
  const lines = withLineBreaks(markdown.replace(/\r\n?/g, '\n')).split('\n');
  const parts: string[] = [];
  let paragraph: string[] = [];
  let saidCode = false;
  let saidTable = false;
  const flush = () => {
    if (paragraph.length > 0) parts.push(sentence(inline(paragraph.join(' '), t)));
    paragraph = [];
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i] ?? '';
    const fence = FENCE.exec(line);
    if (fence) {
      flush();
      const marker = fence[1] ?? '```';
      while (i + 1 < lines.length && !(lines[i + 1] ?? '').trim().startsWith(marker)) i += 1;
      i += 1; // the closing fence (or the end of an unfinished block)
      if (!ARTIFACT_FENCE.test(fence[2] ?? '') && !saidCode) {
        parts.push(t('speechCode'));
        saidCode = true;
      }
      continue;
    }
    if (line.includes('|') && TABLE_DIVIDER.test(lines[i + 1] ?? '')) {
      flush();
      i += 1;
      while (i + 1 < lines.length && (lines[i + 1] ?? '').includes('|')) i += 1;
      if (!saidTable) {
        parts.push(t('speechTable'));
        saidTable = true;
      }
      continue;
    }
    const text = line.replace(/^\s*>\s?/, '');
    if (!text.trim() || RULE.test(text)) {
      flush();
      continue;
    }
    if (LIST_ITEM.test(text) || HEADING.test(text)) {
      flush();
      parts.push(sentence(inline(text.replace(LIST_ITEM, '').replace(HEADING, ''), t)));
      continue;
    }
    paragraph.push(text);
  }
  flush();
  return parts
    .filter(Boolean)
    .join(' ')
    .replace(/\s+([.,!?;:])/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

function inline(text: string, t: (key: SpeechKey) => string): string {
  return (
    text
      .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
      .replace(/\[\^[^\]]*\]/g, '')
      .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
      .replace(/<(?:https?:\/\/|mailto:)[^>\s]+>/gi, ` ${t('speechLink')} `)
      .replace(/<\/?[a-z][^>]*>/gi, ' ')
      .replace(/\b(?:https?:\/\/|www\.)[^\s)]+/gi, t('speechLink'))
      .replace(/`([^`]*)`/g, '$1')
      .replace(/(\*\*|__|~~)(.+?)\1/g, '$2')
      .replace(/(^|[^\w*])[*_](\S(?:.*?\S)?)[*_](?=[^\w*]|$)/g, '$1$2')
      .replace(/\\([\\`*_{}[\]()#+\-.!|>])/g, '$1')
      // Markdown symbols left over; a # or > only when it stands alone ("C#" stays).
      .replace(/[|*`~]+/g, ' ')
      .replace(/(^|\s)[#>]+(?=\s|$)/g, '$1')
      .replace(/\s+/g, ' ')
      .trim()
  );
}

function sentence(text: string): string {
  if (!text) return '';
  return END_PUNCTUATION.test(text) ? text : `${text}.`;
}

/**
 * Splits speech text into clips of at most `max` characters on sentence boundaries, so the
 * first clip can play while the next one is fetched. A sentence longer than `max` is cut at a
 * comma or space.
 */
export function speechChunks(text: string, max = 600): string[] {
  const sentences = text.match(/[^.!?…]+(?:[.!?…]+["'”’)\]]*|$)\s*/g) ?? [];
  const chunks: string[] = [];
  let current = '';
  const push = () => {
    if (current.trim()) chunks.push(current.trim());
    current = '';
  };
  for (const raw of sentences) {
    let part = raw;
    while (part.trim().length > max) {
      push();
      const window = part.slice(0, max);
      const cut = Math.max(window.lastIndexOf(', '), window.lastIndexOf(' '));
      const at = cut > max / 2 ? cut + 1 : max;
      chunks.push(part.slice(0, at).trim());
      part = part.slice(at);
    }
    if ((current + part).trim().length > max) push();
    current += part;
  }
  push();
  return chunks;
}

/** An answer this short is read as it is: a summary would take longer than the answer. */
export const SHORT_ANSWER_CHARS = 320;

/**
 * The first sentences of `text`, up to `max` characters: the spoken answer when no summary
 * could be made. Never cuts a sentence in two unless the first one alone is longer than `max`.
 */
export function leadSentences(text: string, max = 420): string {
  const sentences = text.match(/[^.!?…]+(?:[.!?…]+["'”’)\]]*|$)\s*/g) ?? [];
  let out = '';
  for (const sentence of sentences) {
    if (out && (out + sentence).trim().length > max) break;
    out += sentence;
  }
  const lead = out.trim();
  return lead.length > max ? `${lead.slice(0, max).trimEnd()}…` : lead;
}
